import { Injectable } from '@angular/core';

import { StorageListResult } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { Constants } from '../constants/constants';
import { WidgetDataSource } from '../models';
import { AnalysisGroupService } from '../providers/storage-api/analysis-group-service';
import {
  CollectionService,
  SectionMetadata,
  SectionMetadataCardinality,
} from '../providers/storage-api/collection.service';

export interface SelectedSectionValidation {
  field: string;
  buckets: string[];
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class SectionService {
  constructor(
    private collectionService: CollectionService,
    private analysisGroupService: AnalysisGroupService
  ) {}

  public async listSectionMetadata(
    projectId: string,
    widgetDataSource: WidgetDataSource
  ): Promise<SectionMetadata[]> {
    const result: SectionMetadata[] = [];

    const cardinalityListResult = await this.getSectionMetadataCardinalityByWidgetDataSource(
      projectId,
      widgetDataSource
    );

    const sectionMetadataCardinalities = cardinalityListResult.items.filter(
      (c) => c.cardinality <= Constants.MAX_SECTION_CARDINALITY
    );

    for (const sectionMetadataCardinality of sectionMetadataCardinalities) {
      const sectionMetadata = await this.getSectionMetadataTopNByWidgetDataSource(
        projectId,
        widgetDataSource,
        sectionMetadataCardinality.fieldName
      );

      result.push({
        ...sectionMetadata,
        type: 'metadata',
      });
    }

    return result;
  }

  private async getSectionMetadataCardinalityByWidgetDataSource(
    projectId: string,
    widgetDataSource: WidgetDataSource
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    if (widgetDataSource.groupId) {
      return await this.analysisGroupService.getSectionMetadataCardinality(
        projectId,
        widgetDataSource.groupId
      );
    }

    return await this.collectionService.getSectionMetadataCardinality(
      projectId,
      widgetDataSource.collectionName
    );
  }

  private async getSectionMetadataTopNByWidgetDataSource(
    projectId: string,
    widgetDataSource: WidgetDataSource,
    fieldName: string
  ): Promise<SectionMetadata> {
    if (widgetDataSource.groupId) {
      return await this.analysisGroupService.getSectionMetadataTopN(
        projectId,
        widgetDataSource.groupId,
        fieldName
      );
    }

    return await this.collectionService.getSectionMetadataTopN(
      projectId,
      widgetDataSource.collectionName,
      fieldName
    );
  }

  public async loadSectionProperties(
    projectId: string,
    widgetDataSource: WidgetDataSource,
    minCardinality: number = 1
  ): Promise<SectionMetadata[]> {
    const result: SectionMetadata[] = [];

    const cardinalityListResult = await this.getSectionPropertyCardinalityByWidgetDataSource(
      projectId,
      widgetDataSource
    );

    const sectionPropertyCardinalities = cardinalityListResult.items.filter(
      (c) =>
        c.cardinality >= minCardinality &&
        c.cardinality <= Constants.MAX_SECTION_CARDINALITY &&
        Constants.SECTION_ALLOWED_PROPERTY_TYPES.includes(c.fieldName)
    );

    for (const sectionMetadataCardinality of sectionPropertyCardinalities) {
      const sectionMetadata = await this.getSectionMetadataTopNByWidgetDataSource(
        projectId,
        widgetDataSource,
        sectionMetadataCardinality.fieldName
      );

      result.push({
        ...sectionMetadata,
        type: 'property',
      });
    }

    return result;
  }

  public async getSectionPropertyCardinality(
    projectId: string,
    widgetDataSource: WidgetDataSource
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    return await this.getSectionPropertyCardinalityByWidgetDataSource(projectId, widgetDataSource);
  }

  private async getSectionPropertyCardinalityByWidgetDataSource(
    projectId: string,
    widgetDataSource: WidgetDataSource
  ): Promise<StorageListResult<SectionMetadataCardinality>> {
    if (widgetDataSource.groupId) {
      return await this.analysisGroupService.getSectionPropertyCardinality(
        projectId,
        widgetDataSource.groupId
      );
    }

    return await this.collectionService.getSectionPropertyCardinality(
      projectId,
      widgetDataSource.collectionName
    );
  }

  /**
   * Loads all section metadata (both metadata and properties) in a single call.
   * This consolidates the common pattern used across widget-add, widget-filter,
   * and widget-analytic-item-dialog components.
   */
  public async loadAllSectionMetadata(
    projectId: string,
    widgetDataSource: WidgetDataSource
  ): Promise<SectionMetadata[]> {
    // DASH-9784: Include every metadata, but only allowed property types
    const includes = ['metadata.*', ...Constants.SECTION_ALLOWED_PROPERTY_TYPES];

    const result = widgetDataSource.groupId
      ? await this.analysisGroupService.getSectionMetadataAllReport(
          projectId,
          widgetDataSource.groupId,
          includes
        )
      : await this.collectionService.getSectionMetadataAllReport(
          projectId,
          widgetDataSource.collectionName,
          includes
        );

    return result?.items ?? [];
  }

  /**
   * Validates that all selected sections have both a field and at least one bucket selected.
   */
  public static isSelectionComplete(selectedSections?: SelectedSectionValidation[]): boolean {
    if (!selectedSections || selectedSections.length === 0) {
      return true;
    }
    return !selectedSections.some(
      (sec) => !sec.field || !Array.isArray(sec.buckets) || sec.buckets.length === 0
    );
  }
}
